import { useCallback, useState } from 'react';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Collapse, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconLayoutSidebar } from '@tabler/icons-react';
import { Logo } from '../Logo';
import { navSections, settingsItem, type NavItem } from './navItems';

// Tiny class-name joiner; we don't pull clsx for one call site.
const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ');

const SECTIONS_KEY = 'wheels.sidebar.sections';

function loadCollapsedSections(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(SECTIONS_KEY) ?? '{}') as Record<string, boolean>;
  } catch {
    return {};
  }
}

interface SidebarProps {
  /** Icon-only rail mode. */
  mini: boolean;
  onToggleMini: () => void;
}

export function Sidebar({ mini, onToggleMini }: SidebarProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapsedSections);

  const toggleSection = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(SECTIONS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <nav className="flex h-full flex-col text-[14px]">
      <BrandHeader mini={mini} />
      <div className={cx('flex-1 overflow-y-auto py-4', mini ? 'px-2' : 'px-3')}>
        <Stack gap={mini ? 'sm' : 'lg'}>
          {navSections.map((section) => (
            <div key={section.titleKey}>
              {mini ? (
                <div className="mx-2 mb-1 border-t border-white/5 first:hidden" />
              ) : (
                <SectionHeader
                  title={t(section.titleKey)}
                  collapsed={!!collapsed[section.titleKey]}
                  onToggle={() => toggleSection(section.titleKey)}
                />
              )}
              <Collapse in={mini || !collapsed[section.titleKey]}>
                <ul className={cx('m-0 list-none p-0 space-y-0.5', !mini && 'mt-2')}>
                  {section.items.map((item) => (
                    <SidebarLink key={item.to} item={item} mini={mini} />
                  ))}
                </ul>
              </Collapse>
            </div>
          ))}
        </Stack>
      </div>
      <div className={cx('border-t border-white/5 py-3', mini ? 'px-2' : 'px-3')}>
        <ul className="m-0 list-none p-0 space-y-0.5">
          <SidebarLink item={settingsItem} mini={mini} />
        </ul>
        <UnstyledButton
          onClick={onToggleMini}
          title={t(mini ? 'nav.sidebar.expand' : 'nav.sidebar.minimize')}
          className={cx(
            'mt-1 flex w-full items-center gap-3 rounded-md px-3 py-2 transition-colors',
            'text-[var(--wheels-sidebar-ink-muted)] hover:bg-[var(--wheels-sidebar-active)] hover:text-white',
            mini && 'justify-center px-0',
          )}
        >
          <IconLayoutSidebar size={18} stroke={1.6} className="shrink-0 opacity-80" />
          {!mini && <span>{t('nav.sidebar.minimize')}</span>}
        </UnstyledButton>
      </div>
    </nav>
  );
}

function BrandHeader({ mini }: { mini: boolean }) {
  const { t } = useTranslation();
  return (
    <div
      className={cx(
        'flex items-center gap-3 overflow-hidden border-b border-white/5 py-4',
        mini ? 'justify-center px-2' : 'px-5',
      )}
    >
      {mini ? (
        // The wordmark doesn't fit a 68px rail — a compact monogram does.
        <span
          className="text-white"
          style={{ fontWeight: 700, fontSize: 18, letterSpacing: '0.02em', lineHeight: 1 }}
        >
          W
        </span>
      ) : (
        <>
          <Logo height={22} variant="on-dark" />
          <div className="hidden text-xs text-[var(--wheels-sidebar-ink-muted)] lg:block">
            {t('app.tagline')}
          </div>
        </>
      )}
    </div>
  );
}

function SectionHeader({
  title, collapsed, onToggle,
}: { title: string; collapsed: boolean; onToggle: () => void }) {
  return (
    <UnstyledButton
      onClick={onToggle}
      className="flex w-full items-center justify-between rounded px-3 py-1 hover:bg-white/5"
    >
      <Text
        tt="uppercase"
        fz={11}
        fw={600}
        lts="0.06em"
        c="var(--wheels-sidebar-ink-muted)"
      >
        {title}
      </Text>
      <IconChevronDown
        size={13}
        stroke={1.8}
        className={cx(
          'text-[var(--wheels-sidebar-ink-muted)] transition-transform',
          collapsed && 'ltr:-rotate-90 rtl:rotate-90',
        )}
      />
    </UnstyledButton>
  );
}

function SidebarLink({ item, mini }: { item: NavItem; mini: boolean }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const Icon = item.icon;
  // Exact match for "/", prefix-match for everything else so /items and
  // /items/{id}/edit both highlight the Items entry.
  const isActive =
    item.to === '/'
      ? location.pathname === '/'
      : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

  const link = (
    <RouterNavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive: routerActive }) =>
        cx(
          // no-underline on every state — Mantine's global anchor
          // styles otherwise underline router links.
          'flex items-center gap-3 rounded-md py-2 transition-colors',
          'no-underline hover:no-underline focus:no-underline',
          mini ? 'justify-center px-0' : 'px-3',
          (isActive || routerActive)
            ? 'bg-[var(--wheels-sidebar-active)] font-medium text-white'
            : 'text-[var(--wheels-sidebar-ink)] hover:bg-[var(--wheels-sidebar-active)] hover:text-white',
        )
      }
    >
      <Icon size={18} stroke={1.6} className="shrink-0 opacity-80" />
      {!mini && <span>{t(item.labelKey)}</span>}
    </RouterNavLink>
  );

  return (
    <li>
      {mini ? (
        <Tooltip
          label={t(item.labelKey)}
          position={i18n.dir() === 'rtl' ? 'left' : 'right'}
          withArrow
        >
          {link}
        </Tooltip>
      ) : (
        link
      )}
    </li>
  );
}
